import React, { HTMLAttributes, forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface ThemedCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Custom background color class */
  colorClass?: string;
  /** Whether the card is interactive (adds hover effects) */
  interactive?: boolean;
  /** Card padding size */
  padding?: "none" | "sm" | "md" | "lg";
}

export const ThemedCard = forwardRef<HTMLDivElement, ThemedCardProps>(
  ({ colorClass, interactive = false, padding = "md", className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          // Base styles
          colorClass || "bg-white",
          // Theme-aware border and shadow
          "border-theme rounded-theme-lg shadow-theme",
          // Interactive styles
          interactive && "hover:shadow-theme-hover transition-all cursor-pointer",
          // Padding
          padding === "sm" && "p-3",
          padding === "md" && "p-4",
          padding === "lg" && "p-6",
          padding === "none" && "",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ThemedCard.displayName = "ThemedCard";

// Card Header component
export interface ThemedCardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Custom background color class */
  colorClass?: string;
}

export const ThemedCardHeader = forwardRef<HTMLDivElement, ThemedCardHeaderProps>(
  ({ colorClass, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          colorClass || "bg-gray-100",
          "px-4 py-3",
          "border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ThemedCardHeader.displayName = "ThemedCardHeader";

// Card Content component
export const ThemedCardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("p-4", className)} {...props}>
        {children}
      </div>
    );
  }
);

ThemedCardContent.displayName = "ThemedCardContent";
